import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import {
    listUserQueryProject,
    addUserQueryProject,
    updateUserQueryProject,
    deleteUserQueryProject,
} from "../../Redux/Actions/userQueryProjectActions";
import { columnsUserQueryProjectList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import { Modal, Button } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import UserQueryProjectEditModal from "./addAndEditUserQueryProject";
import UserQueryProjectViewModal from "./viewUserQueryProject";
import DateRangePopup from "../../Utils/dateFilterPopup";

const { confirm } = Modal;

const ListUserQueryProject = () => {
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedViewRecord, setSelectedViewRecord] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const fetchDetails = useCallback(
        (page = pagination.current, pageSize = pagination.pageSize) => {
            const payload = {
                page,
                limit: pageSize,
                filter: debouncedSearch,
                is_deleted: false,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            return dispatch(listUserQueryProject(payload)).catch(() => {
                toast.error("Failed to fetch user query project list");
            });
        },
        [dispatch, debouncedSearch, dateRange, pagination.current, pagination.pageSize]
    );

    const uqpState = useSelector((state) => state.userQueryProject || {});
    const records = useMemo(() => {
        return uqpState.userQueryProjectList?.data || [];
    }, [uqpState.userQueryProjectList]);
    const total = useMemo(() => {
        return uqpState.userQueryProjectList?.total || 0;
    }, [uqpState.userQueryProjectList]);
    const { listLoading } = uqpState;

    useEffect(() => {
        fetchDetails(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, fetchDetails]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [debouncedSearch, dateRange]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, total }));
    }, [total]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const openEditModal = useCallback((record = null) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    }, []);

    const closeEditModal = () => {
        setSelectedRecord(null);
        setIsModalOpen(false);
    };

    const openViewModal = useCallback((record = null) => {
        setSelectedViewRecord(record);
        setIsViewModalOpen(true);
    }, []);

    const closeViewModal = () => {
        setSelectedViewRecord(null);
        setIsViewModalOpen(false);
    };

    const handleSubmit = async (values) => {
        try {
            let result;
            if (selectedRecord?._id) {
                result = await dispatch(updateUserQueryProject(selectedRecord._id, values));
            } else {
                result = await dispatch(addUserQueryProject(values));
            }

            if (result?.statusCode === 200) {
                await fetchDetails(pagination.current, pagination.pageSize);
                closeEditModal();
                return { success: true };
            }
        } catch (error) {
            toast.error("Failed to save entry");
            return { success: false };
        }
    };

    const handleDelete = (record) => {
        confirm({
            title: "Delete this entry?",
            icon: <ExclamationCircleFilled />,
            content: `This will remove "${record.project_name}" from the list.`,
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                const res = await dispatch(deleteUserQueryProject(record._id));
                if (res?.statusCode === 200) {
                    fetchDetails(pagination.current, pagination.pageSize);
                }
            },
        });
    };

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const handleDownloadExcel = async () => {
        try {
            const payload = {
                page: 1,
                limit: total || 1000,
                filter: debouncedSearch,
                is_deleted: false,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };

            const res = await dispatch(listUserQueryProject(payload));
            const rawData = res?.data || [];

            if (!Array.isArray(rawData) || rawData.length === 0) {
                toast.error("No data available to download");
                return;
            }

            const preparedData = rawData.map((item) => ({
                // _id: item._id,
                project_name: item.project_name,
                builder_name: item.builder_name,
                street_address: item.street_address,
                user_name: item.user?.name || "",
                // user_email: item.user?.email || "",
                // user_contact: item.user?.contact_number || "",
                // created_at: item.created_at,
            }));

            const worksheet = XLSX.utils.json_to_sheet(preparedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "User Query Projects");
            XLSX.writeFile(workbook, "user_query_projects.xlsx");
        } catch (error) {
            toast.error("Failed to download data");
        }
    };

    return (
        <>
            <div style={{ position: "absolute", marginTop: "9.6%", marginLeft: "86.5%", display: "flex", gap: 10 }}>
                {canAdd && (
                    <Button
                        style={{ background: "#070C18", color: "white", padding: "18px", borderRadius: "20px" }}
                        onClick={() => openEditModal(null)}
                    >
                        Add Entry
                    </Button>
                )}
            </div>

            <TableComponent
                pagination={pagination}
                setState={setSearchQuery}
                dataSource={records}
                onCalendarClick={() => setIsDatePopupVisible(true)}
                onDownload={canAdd && canEdit ? handleDownloadExcel : undefined}
                onChange={handleTableChange}
                h3heading="User Query Projects"
                columns={columnsUserQueryProjectList(
                    pagination.current,
                    pagination.pageSize,
                    openViewModal,
                    openEditModal,
                    handleDelete,
                    canEdit
                )}
                loading={listLoading}
            />

            {isModalOpen && (
                <UserQueryProjectEditModal
                    visible={isModalOpen}
                    recordData={selectedRecord}
                    onSave={handleSubmit}
                    onClose={closeEditModal}
                />
            )}

            {isViewModalOpen && (
                <UserQueryProjectViewModal
                    visible={isViewModalOpen}
                    recordData={selectedViewRecord}
                    onClose={closeViewModal}
                />
            )}

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default ListUserQueryProject;